import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {QueryRef} from 'apollo-angular';
import {Subscription} from 'rxjs';
import {BaseComponent} from '../../base/base.component';
import {UserService} from '../../service/user.service';
import {FilterService} from '../../service/filter.service';
import {
    Country,
    GetCountriesGQL,
    GetCountriesQuery,
    GetUnitiesByCountryGQL,
    GetUnitiesByCountryQuery,
    Unity,
    User
} from '../../../generated/graphql';
import {
    MOCK_CFTV_FAILURE_BY_DIMENSION,
    MOCK_COMPLIANCE_BY_SYSTEM,
    MOCK_CRITICAL_BLOCS,
    MOCK_KPIS,
    MOCK_MONTHLY_EVOLUTION,
    MOCK_NON_CONFORMITY_BY_STATUS
} from './dashboard-electronic-security.mock';

declare var moment;
declare var $;

interface KpiCard {
    label: string;
    value: string;
    hint: string;
    variant: 'default' | 'success' | 'warning' | 'danger';
}

@Component({
    selector: 'app-dashboard-electronic-security',
    templateUrl: './dashboard-electronic-security.component.html',
    styleUrls: ['./dashboard-electronic-security.component.scss']
})
export class DashboardElectronicSecurityComponent extends BaseComponent implements OnInit, OnDestroy {

    filterKey = 'dashboard_electronic_filter';
    user: User = new User();

    subscription: Subscription;

    countries: Array<Country>;
    countryQuery: QueryRef<GetCountriesQuery>;

    unities: Array<Unity>;
    unityQuery: QueryRef<GetUnitiesByCountryQuery>;

    selectedCountryId: string;
    filter: any = {};

    // ----- KPIs -----
    kpiCards: KpiCard[] = [];

    // ----- Dados dos gráficos (preenchidos a partir do mock) -----
    myData1: any[] = [['', 0, 0, 0]];   // conformidade por sistema
    myData2: any[] = [['', 0]];         // não-conformidades por status
    myData3: any[] = [['', 0, 0]];      // evolução mensal
    myData4: any[] = [['', 0]];         // blocos críticos
    myData5: any[] = [['', 0]];         // CFTV falhas por dimensão

    // ----- Nomes de colunas/séries PT/ES (Google Charts não aceita o pipe translate) -----
    chart1Names = ['Sistema', 'OK', 'Erro', 'Falha'];
    chart1NamesES = ['Sistema', 'OK', 'Error', 'Fallo'];

    chart2Names = ['Status', 'Quantidade'];
    chart2NamesES = ['Estado', 'Cantidad'];

    chart3Names = ['Mês', 'Abertas', 'Resolvidas'];
    chart3NamesES = ['Mes', 'Abiertas', 'Resueltas'];

    chart4Names = ['Bloco', 'Não conformidades'];
    chart4NamesES = ['Bloque', 'No conformidades'];

    chart5Names = ['Dimensão', 'Câmeras com erro'];
    chart5NamesES = ['Dimensión', 'Cámaras con error'];

    columnNames1: string[] = [];
    columnNames2: string[] = [];
    columnNames3: string[] = [];
    columnNames4: string[] = [];
    columnNames5: string[] = [];

    // ----- Opções (paleta Eurofarma) -----
    // 100% empilhado: compara CONFORMIDADE entre sistemas (não volume de equipamentos).
    options1 = {
        isStacked: 'percent',
        legend: {position: 'top'},
        vAxis: {minValue: 0, maxValue: 1, format: 'percent', gridlines: {count: 5}},
        colors: ['#032E58', '#FAE100', '#C0392B']
    };
    // Ordem das fatias: pending, working, late(VERMELHO=alerta), done.
    options2 = {legend: {position: 'right'}, colors: ['#53565A', '#004B93', '#C0392B', '#032E58']};
    options3 = {legend: {position: 'top'}, vAxis: {minValue: 0}, colors: ['#032E58', '#FAE100']};
    options4 = {legend: {position: 'none'}, hAxis: {minValue: 0, format: '0', gridlines: {count: -1}}, colors: ['#004B93']};
    options5 = {
        legend: {position: 'none'},
        vAxis: {minValue: 0, format: '0', gridlines: {count: -1}},
        bar: {groupWidth: '45%'},
        colors: ['#032E58']
    };

    constructor(public router: Router,
                public translate: TranslateService,
                public userService: UserService,
                private getCountriesGQL: GetCountriesGQL,
                private getUnitiesByCountryGQL: GetUnitiesByCountryGQL,
                private filterService: FilterService) {
        super(router, translate);
        // Ao trocar o idioma no header, re-renderiza rótulos das colunas e labels
        // embutidos nos dados (nomes de sistema/status/dimensão) — sem reload.
        this.subscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            $(() => {
                this.checkSubtitle();
                this.buildKpis();
                this.buildCharts();
            });
        });
    }

    ngOnInit(): void {
        this.userService.user.subscribe({
            next: data => {
                this.user = data as User;
            }, error: err => super.onError(err)
        });

        this.getCountries();
        this.getUnitiesInit();

        const savedFilter = this.filterService.getFilter(this.filterKey);
        if (savedFilter) {
            this.filter = savedFilter;
            this.selectedCountryId = savedFilter.countryId;
            if (savedFilter.countryId) {
                this.getUnities();
            }
        } else {
            this.setDefaultPeriod();
        }

        this.initDatePicker();

        this.checkSubtitle();
        this.buildKpis();
        this.buildCharts();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    setDefaultPeriod() {
        // Default: últimos 30 dias.
        this.filter.start = moment().startOf('day').subtract(30, 'days').utcOffset(0, true).format();
        this.filter.end = moment().endOf('day').utcOffset(0, true).format();
    }

    initDatePicker() {
        this.setDatePickerRange('dashboard-daterangepicker', (start, end, label) => {
            this.filter.start = start.utcOffset(0, true).format();
            this.filter.end = end.utcOffset(0, true).format();
        }, this.filter.start, this.filter.end);
    }

    getCountries() {
        this.countryQuery = this.getCountriesGQL.watch();
        this.countryQuery.valueChanges.subscribe(({data}) => {
            this.countries = data.countries as Array<Country>;
        });
    }

    getUnitiesInit() {
        this.unityQuery = this.getUnitiesByCountryGQL.watch({countryId: this.isNullOrUndefined(this.selectedCountryId) ? '' : this.selectedCountryId});
        this.unityQuery.valueChanges.subscribe(({data}) => {
            this.unities = data.getUnitiesByCountry as Array<Unity>;
        });
    }

    getUnities() {
        this.unityQuery.refetch({countryId: this.isNullOrUndefined(this.selectedCountryId) ? '' : this.selectedCountryId});
    }

    applyFilter() {
        if (!this.isNullOrUndefined(this.selectedCountryId)) {
            this.filter.countryId = this.selectedCountryId;
        } else {
            this.filter.countryId = undefined;
        }
        this.filterService.setFilter(this.filterKey, this.filter);

        // Nesta fase os dados são mockados: só re-renderiza os mesmos números
        // (o loader dá o feedback visual). NÃO varia valores — seria enganoso.
        super.toggleLoader(true);
        this.buildKpis();
        this.buildCharts();
        super.toggleLoader(false);
        // TODO(dados-reais): aplicar filtro
    }

    clearFilter() {
        this.filterService.clearFilter(this.filterKey);
        this.filter = {};
        this.selectedCountryId = null;
        this.setDefaultPeriod();
    }

    // ---------------------------------------------------------------------
    // Montagem dos KPIs e gráficos a partir do MOCK
    // ---------------------------------------------------------------------
    buildKpis() {
        const k = MOCK_KPIS;
        const t = (key: string) => this.translate.instant('dashboards.electronicSecurity.' + key);
        this.kpiCards = [
            {
                label: t('kpi.activeEquipment'),
                value: this.fmtInt(k.activeEquipment),
                hint: `${k.accessCount} ${t('units.access')} · ${k.alarmCount} ${t('units.alarm')} · ${k.cameraCount} ${t('units.camera')}`,
                variant: 'default'
            },
            {label: t('kpi.checklists'), value: this.fmtInt(k.checklistsInPeriod), hint: '', variant: 'default'},
            {label: t('kpi.openNc'), value: this.fmtInt(k.openNonConformities), hint: '', variant: 'warning'},
            {label: t('kpi.lateItems'), value: this.fmtInt(k.lateItems), hint: '', variant: 'danger'},
            {
                label: t('kpi.compliance'),
                value: this.fmtPercent(k.compliancePercent),
                hint: `+${this.fmtDecimal(k.compliancePercentDelta)} p.p. ${t('kpi.vsPrevious')}`,
                variant: 'success'
            },
            {
                label: t('kpi.avgResolution'),
                value: `${this.fmtDecimal(k.avgResolutionDays)} ${t('kpi.daysUnit')}`,
                hint: '',
                variant: 'default'
            },
        ];
    }

    buildCharts() {
        this.myData1 = MOCK_COMPLIANCE_BY_SYSTEM.map(r => [this.systemLabel(r.system), r.ok, r.error, r.fail]);
        this.myData2 = MOCK_NON_CONFORMITY_BY_STATUS.map(r => [this.statusLabel(r.status), r.count]);
        this.myData3 = MOCK_MONTHLY_EVOLUTION.map(r => [r.month, r.opened, r.resolved]);
        this.myData4 = MOCK_CRITICAL_BLOCS.map(r => [r.bloc, r.nonConformities]);
        this.myData5 = MOCK_CFTV_FAILURE_BY_DIMENSION.map(r => [this.dimensionLabel(r.dimension), r.camerasWithError]);
    }

    checkSubtitle() {
        if (this.translate.currentLang === 'pt') {
            this.columnNames1 = this.chart1Names;
            this.columnNames2 = this.chart2Names;
            this.columnNames3 = this.chart3Names;
            this.columnNames4 = this.chart4Names;
            this.columnNames5 = this.chart5Names;
        } else {
            this.columnNames1 = this.chart1NamesES;
            this.columnNames2 = this.chart2NamesES;
            this.columnNames3 = this.chart3NamesES;
            this.columnNames4 = this.chart4NamesES;
            this.columnNames5 = this.chart5NamesES;
        }
    }

    // ---- labels de categoria (eixo X / fatias) — traduzidos via i18n ----
    systemLabel(system: string): string {
        return this.translate.instant('dashboards.electronicSecurity.systems.' + system);
    }

    statusLabel(status: string): string {
        return this.translate.instant('dashboards.electronicSecurity.status.' + status);
    }

    dimensionLabel(dimension: string): string {
        return this.translate.instant('dashboards.electronicSecurity.dims.' + dimension);
    }

    // ---- formatação numérica (vírgula decimal, PT/ES) ----
    fmtInt(n: number): string {
        return n.toString();
    }

    fmtDecimal(n: number): string {
        return n.toFixed(1).replace('.', ',');
    }

    fmtPercent(n: number): string {
        return this.fmtDecimal(n) + '%';
    }
}
